/**
 * Token CSRF del panel de administración.
 *
 * El backend (ver `emitir_csrf` y `get_current_user` en `backend/server.py`)
 * exige, en toda petición que muta estado y viene autenticada por cookie, una
 * cabecera `X-CSRF-Token` que coincida con la cookie `csrf_token`.
 *
 * El intento anterior leía esa cookie con `document.cookie` y nunca la
 * encontraba: el panel se sirve desde `evangelizaciondigitalhuelva.org` y el
 * backend desde `api.evangelizaciondigitalhuelva.org`. Son el mismo *sitio*
 * —por eso la cookie de sesión sí viaja—, pero no el mismo *origen*, y
 * `document.cookie` solo ve las cookies del host del documento. Resultado: la
 * cabecera no se enviaba jamás y cada guardado del panel moría con
 * «Token CSRF inválido o ausente».
 *
 * Ahora el token llega en el cuerpo de `/auth/login` (y de `/auth/csrf`), se
 * guarda en `localStorage` y se reenvía como cabecera. La cookie la sigue
 * mandando el navegador por su cuenta, así que el doble envío —cookie del
 * navegador contra cabecera del panel— continúa cumpliendo su función.
 */
import axios from "axios";
import { WEB_BACKEND } from "@/lib/backends";

const CLAVE = "csrf_token";
const CABECERA = "X-CSRF-Token";
const API = `${WEB_BACKEND}/api`;
const METODOS_QUE_MUTAN = ["post", "put", "patch", "delete"];

/**
 * En desarrollo el panel y el backend comparten origen (el proxy de
 * `craco.config.js`), y ahí la cookie sí es legible. Se prefiere a lo guardado
 * porque es la que el backend va a comparar.
 */
function leerDeCookie() {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function leerDeAlmacen() {
  try {
    return window.localStorage.getItem(CLAVE) || null;
  } catch {
    // Safari en navegación privada puede lanzar al tocar localStorage.
    return null;
  }
}

/** Token vigente, o `null` si no hay sesión o aún no se ha pedido. */
export function tokenCsrf() {
  return leerDeCookie() || leerDeAlmacen();
}

/** Guarda el token recibido del backend. */
export function fijarCsrf(token) {
  try {
    if (token) window.localStorage.setItem(CLAVE, token);
    else window.localStorage.removeItem(CLAVE);
  } catch {
    /* almacenamiento bloqueado: el token vive solo en esta página */
  }
  return token;
}

/** Olvida el token. Se llama al cerrar sesión. */
export function olvidarCsrf() {
  fijarCsrf(null);
}

/** ¿Es este error el 403 de CSRF del backend, y no un 403 de permisos? */
export function esErrorCsrf(err) {
  if (err?.response?.status !== 403) return false;
  const detalle = err?.response?.data?.detail;
  return typeof detalle === "string" && detalle.toUpperCase().includes("CSRF");
}

/**
 * Solo se manda el token a nuestro backend, nunca a terceros.
 *
 * Importa de verdad: `subir-archivo.js` sube el fichero directamente a
 * Cloudinary con la misma instancia de axios. Se comparan orígenes, no
 * prefijos de texto, para que `https://api.evangelizaciondigitalhuelva.org.…`
 * de un tercero no cuele como propio.
 */
function esUrlPropia(url) {
  if (!url) return false;
  if (!/^https?:\/\//i.test(url)) return true; // relativa: mismo origen
  try {
    return new URL(url).origin === new URL(WEB_BACKEND).origin;
  } catch {
    return false;
  }
}

// Una sola petición de refresco aunque fallen varias a la vez: el panel guarda
// secciones enteras de golpe y arrancaría un refresco por cada una.
let refrescoEnCurso = null;

/**
 * Pide al backend un token válido para la sesión actual.
 *
 * Repara el caso de la sesión abierta antes de que existiera la protección
 * CSRF —cookie de sesión buena, cookie `csrf_token` inexistente— sin obligar a
 * cerrar y volver a abrir sesión. Devuelve `null` si no hay sesión.
 */
export function refrescarCsrf() {
  if (refrescoEnCurso) return refrescoEnCurso;
  refrescoEnCurso = axios
    .get(`${API}/auth/csrf`, { withCredentials: true })
    .then((res) => fijarCsrf(res?.data?.csrf_token || null))
    .catch(() => null)
    .finally(() => {
      refrescoEnCurso = null;
    });
  return refrescoEnCurso;
}

/**
 * Instala en una instancia de axios el envío de la cabecera y el reintento.
 *
 * Va como interceptor y no como `axios.defaults.headers.common` porque ese
 * default se copia a *toda* petición, incluida la subida directa a Cloudinary
 * de `subir-archivo.js`: mandaríamos nuestro token a un tercero y forzaríamos
 * un preflight innecesario en su API.
 */
export function instalarCsrf(instancia) {
  instancia.interceptors.request.use((config) => {
    const metodo = (config.method || "get").toLowerCase();
    if (!METODOS_QUE_MUTAN.includes(metodo)) return config;
    if (!esUrlPropia(config.url)) return config;
    const token = tokenCsrf();
    if (token) {
      if (config.headers?.set) config.headers.set(CABECERA, token);
      else config.headers = { ...(config.headers || {}), [CABECERA]: token };
    }
    return config;
  });

  instancia.interceptors.response.use(
    (res) => res,
    async (err) => {
      const original = err?.config;
      // Un solo reintento: si el token recién pedido tampoco vale, el problema
      // no es el token y hay que dejar que el error llegue a la pantalla.
      if (!original || original.__reintentoCsrf || !esErrorCsrf(err)) throw err;
      const token = await refrescarCsrf();
      if (!token) throw err;
      original.__reintentoCsrf = true;
      if (original.headers?.set) original.headers.set(CABECERA, token);
      else original.headers = { ...(original.headers || {}), [CABECERA]: token };
      return instancia.request(original);
    }
  );

  return instancia;
}

// La instancia por defecto la usan `subir-archivo.js`, `AuthContext` y las
// páginas públicas: también necesita la cabecera.
instalarCsrf(axios);
